import React, {useMemo, useState} from 'react';
import {useHistory} from "react-router";
import { EventOutlined as EventOutlinedIcon, BlockOutlined as BlockOutlinedIcon } from "@material-ui/icons";

import {JobList} from "components/JobList";
import {BackendJob, HygienistBackendJob} from "components/EditJob";
import {SectionTabs} from "components/SectionTabs";

import {JobBoardTab} from "./types";
import {getJobsByTab, useClearJobPost} from "./utils";

import {
    Container,
    HeaderRow,
    HeaderText,
    JobListWrap,
    NoJobsMessage,
    StyledAddCircleOutlineIcon,
    StyledButton,
    Subtitle,
    Title,
    TokenIcon,
    TotalPostsCard,
    TotalPostsCount,
    TotalPostsIconWrap,
    TotalPostsLabel,
    TotalPostsTextWrap,
} from './JobBoard.styles';
import clsx from 'clsx';

interface JobBoardProps {
    jobs: BackendJob[];
    dentistUserEmail: string;
    tokensCount: number;
    onTokensClick: () => void;
}

// T2-6042: New header layout — left column shows the page title + subtitle,
// right column shows a "Total Posts" card (briefcase icon, count, label).
// The Active / Inactive tabs are an equal-width segmented control aligned
// with the Post a New Job button below.
const JobBoard = ({ dentistUserEmail, jobs, tokensCount, onTokensClick }: JobBoardProps) => {
    const history = useHistory();
    const [activeTab, setActiveTab] = useState<JobBoardTab>(JobBoardTab.activeJobs);
    const clearJobPost = useClearJobPost(dentistUserEmail);

    const { active, inactive } = useMemo(() => getJobsByTab(jobs), [jobs]);

    const onJobClick = (job: BackendJob | HygienistBackendJob) => {
        history.push({
            pathname: 'jobBoardDetails',
            state: {
                job,
            },
        })
    }

    const onJobAdd = () => {
        clearJobPost();

        history.push({
            pathname: "editJobFirstStep",
            state: { mode: "post" }
        })
    }

    const jobsToDisplay = activeTab === JobBoardTab.activeJobs ? active : inactive;

    const tabs = [
        {
            label: 'Active',
            icon: <EventOutlinedIcon style={{ fontSize: 18 }} />,
            onClick: () => setActiveTab(JobBoardTab.activeJobs),
            isActive: activeTab === JobBoardTab.activeJobs,
        },
        {
            label: 'Inactive',
            icon: <BlockOutlinedIcon style={{ fontSize: 18 }} />,
            onClick: () => setActiveTab(JobBoardTab.inactiveJobs),
            isActive: activeTab === JobBoardTab.inactiveJobs,
        },
    ]

    return (
        <Container id="job-board-container" data-testid="job-board-container" aria-label="job-board-container" content-desc="job-board-container">
            <HeaderRow>
                <HeaderText>
                    <Title id="job-board-title" data-testid="job-board-title" aria-label="job-board-title" content-desc="job-board-title">Your Job Posts</Title>
                    <Subtitle>Manage and track all your job postings.</Subtitle>
                </HeaderText>
                <TotalPostsCard
                    role="button"
                    tabIndex={0}
                    onClick={onTokensClick}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onTokensClick(); }}
                    aria-label={`Total tokens on file: ${tokensCount}`}
                >
                    {/* T2-6042: Holly explicitly asked to keep the existing
                        circle "token" icon (current dev) instead of the
                        ChatGPT-mockup briefcase. */}
                    <TotalPostsIconWrap>
                        <TokenIcon />
                    </TotalPostsIconWrap>
                    <TotalPostsTextWrap>
                        <TotalPostsLabel>Total Tokens</TotalPostsLabel>
                        <TotalPostsCount>{tokensCount}</TotalPostsCount>
                    </TotalPostsTextWrap>
                </TotalPostsCard>
            </HeaderRow>

            <SectionTabs tabs={tabs}/>

            <StyledButton
                variant="contained"
                onClick={onJobAdd}
                color="primary"
                size="large"
                id="add-new-job-button"
                className={clsx("bigButtonInNewStyle", "no-fastclick")}
                aria-label="add-new-job-button"
                data-testid="add-new-job-button"
                content-desc="add-new-job-button"
            >
                <StyledAddCircleOutlineIcon />
                <span>Post a New Job</span>
            </StyledButton>

            <JobListWrap>
                {jobsToDisplay && jobsToDisplay.length ? (
                    <JobList
                        jobs={jobsToDisplay}
                        onJobClick={onJobClick}
                        isActive={activeTab === JobBoardTab.activeJobs}
                    />
                ) : (
                    <NoJobsMessage>
                        You have no {activeTab === JobBoardTab.activeJobs ? 'active' : 'inactive'} jobs posts yet.
                    </NoJobsMessage>
                )}
            </JobListWrap>
        </Container>
    )
}

export default JobBoard;
